import ActivityTimeline from '@/components/profile/ActivityTimeline';
import SecurityActions from '@/components/profile/SecurityActions';
import { InputField } from '@/components/common/form-fields';
import { Input } from '@/components/ui/Input';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import Modal from '@/components/ui/Modal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { profileService, type UpdateProfileRequest } from '@/services/profile.service';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Award, BookOpen, Calendar, Camera, Eye, EyeOff, Loader2, Mail, Save, Trash2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { isValidPhoneNumber } from 'react-phone-number-input';

// Profile Page Skeleton Loader
function ProfileSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Back Button Skeleton */}
      <div className="flex items-center gap-2">
        <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
        <SkeletonLoader type="custom" className="w-32 h-4 rounded" />
      </div>

      {/* Page Header Skeleton */}
      <div>
        <SkeletonLoader type="custom" className="w-48 h-8 rounded mb-2" />
        <SkeletonLoader type="custom" className="w-80 h-4 rounded" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Profile Card & Security */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card Skeleton */}
          <div className="card text-center p-6">
            {/* Profile Picture */}
            <div className="w-32 h-32 mx-auto mb-4">
              <SkeletonLoader type="custom" className="w-full h-full rounded-full" />
            </div>
            {/* User Info */}
            <SkeletonLoader type="custom" className="w-40 h-6 rounded mx-auto mb-2" />
            <SkeletonLoader type="custom" className="w-24 h-4 rounded mx-auto mb-4" />
            <SkeletonLoader type="custom" className="w-32 h-3 rounded mx-auto mb-2" />
            <SkeletonLoader type="custom" className="w-20 h-3 rounded mx-auto" />

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <SkeletonLoader type="custom" className="w-12 h-6 rounded mx-auto mb-1" />
                <SkeletonLoader type="custom" className="w-20 h-3 rounded mx-auto" />
              </div>
              <div className="text-center">
                <SkeletonLoader type="custom" className="w-12 h-6 rounded mx-auto mb-1" />
                <SkeletonLoader type="custom" className="w-16 h-3 rounded mx-auto" />
              </div>
            </div>
          </div>

          {/* Account Info Card Skeleton */}
          <div className="card p-6">
            <SkeletonLoader type="custom" className="w-40 h-5 rounded mb-4" />
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <SkeletonLoader type="custom" className="w-4 h-4 rounded" />
                  <div className="flex-1">
                    <SkeletonLoader type="custom" className="w-16 h-3 rounded mb-1" />
                    <SkeletonLoader type="custom" className="w-24 h-4 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Security Actions Skeleton */}
          <div className="card p-6">
            <SkeletonLoader type="custom" className="w-32 h-5 rounded mb-4" />
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <SkeletonLoader key={i} type="custom" className="w-full h-10 rounded-lg" />
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Profile Form & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Form Skeleton */}
          <div className="card">
            <div className="p-6 border-b flex justify-between items-center">
              <SkeletonLoader type="custom" className="w-40 h-5 rounded" />
              <SkeletonLoader type="custom" className="w-24 h-9 rounded-lg" />
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <SkeletonLoader type="custom" className="w-20 h-4 rounded mb-2" />
                  <SkeletonLoader type="custom" className="w-full h-10 rounded-lg" />
                </div>
                {/* Last Name */}
                <div>
                  <SkeletonLoader type="custom" className="w-20 h-4 rounded mb-2" />
                  <SkeletonLoader type="custom" className="w-full h-10 rounded-lg" />
                </div>
                {/* Email */}
                <div className="md:col-span-2">
                  <SkeletonLoader type="custom" className="w-24 h-4 rounded mb-2" />
                  <SkeletonLoader type="custom" className="w-full h-10 rounded-lg" />
                </div>
                {/* Phone */}
                <div className="md:col-span-2">
                  <SkeletonLoader type="custom" className="w-24 h-4 rounded mb-2" />
                  <SkeletonLoader type="custom" className="w-full h-10 rounded-lg" />
                </div>
              </div>
            </div>
          </div>

          {/* Activity Timeline Skeleton */}
          <div className="card p-6">
            <SkeletonLoader type="custom" className="w-36 h-5 rounded mb-4" />
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-4">
                  <SkeletonLoader type="custom" className="w-10 h-10 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <SkeletonLoader type="custom" className="w-3/4 h-4 rounded mb-2" />
                    <SkeletonLoader type="custom" className="w-1/2 h-3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type ProfileFormData = UpdateProfileRequest;

type PasswordFormData = {
  old_password: string;
  new_password: string;
  confirm_password: string;
};

export default function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateUser } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDeletePictureModal, setShowDeletePictureModal] = useState(false);
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);

  // Profile form
  const { register, handleSubmit, formState: { isSubmitting, errors }, reset, control } = useForm<ProfileFormData>({
    mode: 'onChange',
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { isSubmitting: isPasswordSubmitting, errors: passwordErrors },
    reset: resetPassword,
    watch: watchPassword
  } = useForm<PasswordFormData>({
    mode: 'onChange',
  });

  // Fetch user profile using /me API
  const { data: profileResponse, isLoading, error } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => profileService.getMyProfile(),
  });

  // Update form when profile data is loaded
  useEffect(() => {
    if (profileResponse?.user) {
      reset({
        given_name: profileResponse.user.given_name || '',
        family_name: profileResponse.user.family_name || '',
        email: profileResponse.user.email || '',
        mobile_number: profileResponse.user.mobile_number || '',
      });
    }
  }, [profileResponse, reset]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => profileService.updateMyProfile(data),
    onSuccess: (updatedProfile: any) => {
      queryClient.setQueryData(['userProfile'], updatedProfile);
      const u = updatedProfile?.user ?? updatedProfile;
      if (u) {
        updateUser({
          given_name: u.given_name,
          family_name: u.family_name,
          full_name: u.full_name,
          mobile_number: u.mobile_number,
          profile_image: u.profile_image,
        });
      }
      toast.success('Profile updated successfully!');
      setIsEditMode(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update profile.');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { old_password: string; new_password: string; confirm_password: string }) => {
      if (!profileResponse?.user?.id) {
        throw new Error('User ID not found.');
      }
      return profileService.changePassword(profileResponse.user.id, data);
    },
    onSuccess: () => {
      toast.success('Password changed successfully!');
      resetPassword({ old_password: '', new_password: '', confirm_password: '' });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to change password.');
    },
  });

  // Upload profile picture mutation
  const uploadProfilePicMutation = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);
      const url = await profileService.uploadProfilePicture(file);
      const result = await profileService.updateProfilePicture(url);
      return { result, url };
    },
    onSuccess: ({ result, url }: any) => {
      setIsUploading(false);
      queryClient.setQueryData(['userProfile'], result);
      const newImage = result?.user?.profile_image ?? result?.profile_image ?? url;
      updateUser({ profile_image: newImage });
      toast.success('Profile picture updated successfully!');
    },
    onError: (error: any) => {
      setIsUploading(false);
      toast.error(error.message || 'Failed to upload profile picture.');
    },
  });

  // Delete profile picture mutation
  const deleteProfilePicMutation = useMutation({
    mutationFn: () => profileService.updateProfilePicture(''),
    onSuccess: (updatedProfile: any) => {
      queryClient.setQueryData(['userProfile'], updatedProfile);
      updateUser({ profile_image: '' });
      toast.success('Profile picture removed successfully!');
      setShowDeletePictureModal(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove profile picture.');
    }
  });

  const handleDeleteProfilePicture = () => {
    deleteProfilePicMutation.mutate();
  };

  const handleProfileImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadProfilePicMutation.mutate(file);
    }
    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  // Handle profile update
  const onSubmit = async (data: ProfileFormData) => {
    const profile = profileResponse?.user;

    // Check if mobile number is empty or just country code (send null in that case)
    const getMobileNumber = (value: string | undefined) => {
      if (!value || value.trim() === '') return null;
      const digitsOnly = value.replace(/\D/g, '');
      // If only country code (≤3 digits), treat as empty
      if (digitsOnly.length <= 3) return null;
      return value;
    };

    const mobileNumber = getMobileNumber(data.mobile_number ?? undefined);

    // Check if profile fields have changed
    const hasProfileChanges =
      data.given_name !== (profile?.given_name || '') ||
      data.family_name !== (profile?.family_name || '') ||
      mobileNumber !== (profile?.mobile_number || null);

    if (!hasProfileChanges) {
      toast.error('No changes detected.');
      return;
    }

    updateProfileMutation.mutate({
      given_name: data.given_name,
      family_name: data.family_name,
      mobile_number: mobileNumber,
    });
  };

  // Handle password change
  const onPasswordSubmit = async (data: PasswordFormData) => {
    if (data.new_password !== data.confirm_password) {
      toast.error('New passwords do not match.');
      return;
    }

    changePasswordMutation.mutate({
      old_password: data.old_password,
      new_password: data.new_password,
      confirm_password: data.confirm_password
    });
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to load profile. Please try again.</p>
      </div>
    );
  }

  if (!profileResponse) return null;

  const profile = profileResponse.user;
  const userName = profile.full_name || `${profile.given_name || ''} ${profile.family_name || ''}`.trim();
  const userRole = profile.groups?.[0]?.roles?.[0] || 'User';
  const profileImage = profile.profile_image;
  const userGroups = profile.groups || [];
  const assignedCourseCount = profileResponse.assigned_course_count || 0;
  const completedCourseCount = profileResponse.completed_course_count || 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back Button — goes to the previous page, which isn't always the dashboard */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Back</span>
      </button>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage your personal information and account settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Left Column - Profile Card & Security */}
        <div className="lg:col-span-1 space-y-4 sm:space-y-6 min-w-0">
          <div className="card text-center p-4 sm:p-6">
            {/* Profile Picture */}
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-4">
              <img
                src={profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=3b82f6&color=fff&size=128`}
                alt={userName}
                className={`w-full h-full rounded-full object-cover border-4 border-gray-100 ${isUploading || deleteProfilePicMutation.isPending ? 'opacity-50' : ''}`}
              />
              {profileImage && (
                <button
                  type="button"
                  onClick={() => setShowDeletePictureModal(true)}
                  disabled={deleteProfilePicMutation.isPending || isUploading}
                  className="absolute top-0 right-0 bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 cursor-pointer shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:mt-4"
                  title="Remove profile picture"
                >
                  <Trash2 className="w-3.5 h-3.5 " />
                </button>
              )}
              <label htmlFor="profile-image-upload" className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 cursor-pointer shadow-lg transition-colors">
                {isUploading ?
                  <Loader2 className="w-4 h-4 animate-spin" /> :
                  <Camera className="w-4 h-4" />
                }
              </label>
              <input
                id="profile-image-upload"
                type="file"
                className="hidden"
                onChange={handleProfileImageUpload}
                accept="image/*"
                disabled={isUploading}
              />
            </div>

            {/* User Info */}
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">{userName}</h2>
            <p className="text-gray-600 mb-3 sm:mb-4">{userRole}</p>
            {profile.employee_id && (
              <p className="text-sm text-gray-500">Employee ID: {profile.employee_id}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">Status: {profile.status || 'Active'}</p>

            {/* Quick Stats */}
            <div className="mt-4 sm:mt-6 grid grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <BookOpen className="w-4 h-4 text-blue-600 mr-1" />
                  <span className="text-lg font-semibold text-gray-900">
                    {assignedCourseCount}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Assigned Courses</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Award className="w-4 h-4 text-green-600 mr-1" />
                  <span className="text-lg font-semibold text-gray-900">
                    {completedCourseCount}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Completed</p>
              </div>
            </div>
          </div>

          {/* Additional Info Card */}
          <div className="card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0 break-words">
                  <p className="text-sm text-gray-600">Groups</p>
                  {userGroups.length === 0 ? (
                    <p className="text-sm font-medium text-gray-900">No groups assigned</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1">
                      <p className="text-sm font-medium text-gray-900">
                        {userGroups.slice(0, 2).map(g => g.group_name).join(', ')}
                      </p>
                      {userGroups.length > 2 && (
                        <button
                          type="button"
                          onClick={() => setShowDepartmentsModal(true)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          +{userGroups.length - 2} more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {profile.created_at && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Member Since</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(profile.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {profileResponse.last_login && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Last Login</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(profileResponse.last_login).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Security Actions */}
          <SecurityActions />
        </div>

        {/* Right Column - Profile Form & Activity */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6 min-w-0">
          {/* p-0 on phones: .card already pads, and the header/form pad again
              inside — doubled, the fields were left ~240px wide on a phone. */}
          <div className="card p-0 sm:p-6">
            <div className="p-4 sm:p-6 border-b flex justify-between items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
              <button 
                onClick={() => setIsEditMode(!isEditMode)} 
                className="btn btn-secondary flex-shrink-0"
                type="button"
              >
                {isEditMode ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6" autoComplete="off">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input<ProfileFormData>
                  name="given_name"
                  control={control}
                  label="First Name"
                  type="text"
                  disabled={!isEditMode}
                  autoComplete="off"
                />

                <Input<ProfileFormData>
                  name="family_name"
                  control={control}
                  label="Last Name"
                  type="text"
                  disabled={!isEditMode}
                  autoComplete="off"
                />
                
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address'
                        }
                      })}
                      disabled
                      className={`w-full input-field pl-10 bg-gray-100 ${isEditMode ? 'cursor-not-allowed' : ''}`}
                      autoComplete="off"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email address can't be changed</p>
                </div>
                
                <div className="md:col-span-2">
                  <Controller
                    name="mobile_number"
                    control={control}
                    rules={{
                      validate: (value) => {
                        // Skip validation if empty
                        if (!value || value.trim() === '') return true;
                        // Get digits only to check if user has started entering
                        const digitsOnly = value?.replace(/\D/g, '') || '';
                        // If very few digits (likely just country code), skip validation
                        if (digitsOnly.length <= 3) return true;
                        // Use library's built-in validation for proper country-specific validation
                        return isValidPhoneNumber(value) || 'Please enter a valid mobile number';
                      }
                    }}
                    render={({ field }) => (
                      <InputField
                        label="Phone Number"
                        fieldType="phone"
                        error={errors.mobile_number?.message}
                        phoneValue={field.value || ''}
                        onPhoneChange={(phone) => field.onChange(phone)}
                        phoneCountry="in"
                        disabled={!isEditMode}
                      />
                    )}
                  />
                </div>

              </div>
              
              {isEditMode && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md font-medium"
                    disabled={isSubmitting || updateProfileMutation.isPending}
                  >
                    {isSubmitting || updateProfileMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Change Password Card - Only show if password exists */}
          {profile.is_password_exist && (
            <div className="card p-0 sm:p-6">
              <div className="p-4 sm:p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
              </div>

              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="p-4 sm:p-6 space-y-4 sm:space-y-6" autoComplete="off">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="md:col-span-2 relative">
                    <label htmlFor="old_password" className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      id="old_password"
                      type={showOldPassword ? 'text' : 'password'}
                      {...registerPassword('old_password', {
                        required: 'Current password is required'
                      })}
                      className="w-full input-field pr-10"
                      placeholder="Enter your current password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      className="absolute right-3 top-9 text-gray-500"
                    >
                      {showOldPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    {passwordErrors.old_password && <p className="text-red-500 text-xs mt-1">{passwordErrors.old_password.message}</p>}
                  </div>

                  <div className="relative">
                    <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      id="new_password"
                      type={showPassword ? 'text' : 'password'}
                      {...registerPassword('new_password', {
                        required: 'New password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters long',
                        },
                        pattern: {
                          value: /^(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/,
                          message: 'Password must contain at least one uppercase letter and one special character',
                        },
                      })}
                      className="w-full input-field pr-10"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-9 text-gray-500"
                    >
                      {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    {passwordErrors.new_password && <p className="text-red-500 text-xs mt-1">{passwordErrors.new_password.message}</p>}
                  </div>

                  <div className="relative">
                    <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerPassword('confirm_password', {
                        required: 'Please confirm your new password',
                        validate: value =>
                          value === watchPassword('new_password') || 'Passwords do not match',
                      })}
                      className="w-full input-field pr-10"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-gray-500"
                    >
                      {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    {passwordErrors.confirm_password && <p className="text-red-500 text-xs mt-1">{passwordErrors.confirm_password.message}</p>}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md font-medium"
                    disabled={isPasswordSubmitting || changePasswordMutation.isPending}
                  >
                    {isPasswordSubmitting || changePasswordMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Updating Password...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Update Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Activity Timeline */}
          <ActivityTimeline initialLimit={10} showStats={true} allowPageSizeChange={true} />
        </div>
      </div>

      {/* Delete Profile Picture Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeletePictureModal}
        onClose={() => setShowDeletePictureModal(false)}
        onConfirm={handleDeleteProfilePicture}
        title="Remove Profile Picture"
        description="Are you sure you want to remove your profile picture? Your profile will display a default avatar instead."
        isLoading={deleteProfilePicMutation.isPending}
        confirmButtonText="Remove"
      />

      {/* Departments Modal */}
      <Modal
        isOpen={showDepartmentsModal}
        onClose={() => setShowDepartmentsModal(false)}
        title="Groups"
        subtitle={`You are assigned to ${userGroups.length} groups`}
        size="sm"
      >
        <div className="space-y-3">
          {userGroups.map((group, index) => (
            <div
              key={group.group_key || index}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <Users className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{group.group_name}</p>
                {group.roles && group.roles.length > 0 && (
                  <p className="text-xs text-gray-500">{group.roles.join(', ')}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
